import { useCallback, useState } from "react";

import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { Button } from "../ui/button";

export interface DestructiveConfirmOptions {
  readonly title: string;
  readonly description: string;
  readonly confirmLabel?: string;
}

export interface DestructiveConfirmDialogProps {
  readonly open: boolean;
  readonly options: DestructiveConfirmOptions | null;
  readonly onOpenChange: (open: boolean) => void;
  readonly onConfirm: () => void;
}

/**
 * Pure presentational dialog. The hook below composes this with `useState`
 * machinery; tests render this component directly.
 */
export function DestructiveConfirmDialogView({
  open,
  options,
  onOpenChange,
  onConfirm,
}: DestructiveConfirmDialogProps): React.ReactElement {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogPopup className="max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle>{options?.title ?? ""}</AlertDialogTitle>
          <AlertDialogDescription>{options?.description ?? ""}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogClose render={<Button variant="outline" />}>Cancel</AlertDialogClose>
          <Button variant="destructive" onClick={onConfirm}>
            {options?.confirmLabel ?? "Delete"}
          </Button>
        </AlertDialogFooter>
      </AlertDialogPopup>
    </AlertDialog>
  );
}

interface PendingConfirm {
  readonly options: DestructiveConfirmOptions;
  readonly resolve: (confirmed: boolean) => void;
}

export interface DestructiveConfirmHandle {
  /**
   * Open the dialog and wait for the user. Resolves `true` if the user clicks
   * the destructive button, `false` if they cancel, hit Escape, or click the
   * backdrop.
   */
  readonly confirm: (options: DestructiveConfirmOptions) => Promise<boolean>;
  /** Render this once near the root of the consumer to mount the dialog. */
  readonly DestructiveConfirmDialog: () => React.ReactElement | null;
}

/**
 * Drop-in replacement for `api.dialogs.confirm` that renders an in-app
 * `AlertDialog` instead of native `window.confirm`. Works with Chrome MCP /
 * Playwright (native confirms block the main thread; this does not) and
 * matches the design language used by the project Remove flow.
 *
 * Usage:
 *   const { confirm, DestructiveConfirmDialog } = useDestructiveConfirm();
 *   // ...
 *   const ok = await confirm({ title, description, confirmLabel: "Delete" });
 *   if (!ok) return;
 *   // ...
 *   return <>{children}<DestructiveConfirmDialog /></>;
 */
export function useDestructiveConfirm(): DestructiveConfirmHandle {
  const [pending, setPending] = useState<PendingConfirm | null>(null);

  const confirm = useCallback((options: DestructiveConfirmOptions) => {
    return new Promise<boolean>((resolve) => {
      setPending((previous) => {
        // If a previous confirm is still pending, resolve it as cancel before
        // taking the new one over. Avoids leaking unresolved promises if a
        // caller fires a second confirm before the first dialog closes.
        if (previous) previous.resolve(false);
        return { options, resolve };
      });
    });
  }, []);

  const closeWith = useCallback((confirmed: boolean) => {
    setPending((current) => {
      if (current) current.resolve(confirmed);
      return null;
    });
  }, []);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) closeWith(false);
    },
    [closeWith],
  );

  const handleConfirm = useCallback(() => closeWith(true), [closeWith]);

  const DestructiveConfirmDialog = useCallback(
    () => (
      <DestructiveConfirmDialogView
        open={pending !== null}
        options={pending?.options ?? null}
        onOpenChange={handleOpenChange}
        onConfirm={handleConfirm}
      />
    ),
    [pending, handleOpenChange, handleConfirm],
  );

  return { confirm, DestructiveConfirmDialog };
}
